import { alpha, createTheme, Theme } from '@mui/material/styles';
import defaultLigth from "./defaultLigth";
import defaultDark from "./defaultDark";
import { futuristicThemes } from "./futuristic";

type PaletteOverrides = {
    background?: { paper: string; default: string };
    text?: { primary: string; secondary: string };
};

function createMaterialTheme(
    mode: "light" | "dark",
    primaryMain: string,
    secondaryMain: string,
    overrides: PaletteOverrides = {},
) {
    const isDark = mode === "dark";
    const t = createTheme({
        spacing: 4,
        palette: {
            mode: mode,
            primary: { main: primaryMain },
            secondary: { main: secondaryMain },
            background: overrides.background
                ?? (isDark
                    ? { paper: "#2E3A43", default: "#1D262C" }
                    : { paper: "#f5f5f5", default: "#fafafa" }),
            text: overrides.text
                ?? (isDark
                    ? { primary: "#F4FFFD", secondary: "#bdbdbd" }
                    : { primary: "#263238", secondary: "#546e7a" }),
        },
        typography: {
            fontFamily: `"Montserrat", "Helvetica", "Arial", sans-serif`,
            button: { fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px" },
        },
        shape: { borderRadius: 12 },
    });
    return t;
}

const themes: Record<string, any> = {
    // existing themes — keep both spellings so defaultLight lookup works
    defaultLigth,
    defaultLight: defaultLigth,
    defaultDark,
    // color variants — Light
    materialBlueLigth:   createMaterialTheme("light", "#1565c0", "#42a5f5"),
    materialLimeLigth:   createMaterialTheme("light", "#558b2f", "#aed581"),
    materialOrangeLigth: createMaterialTheme("light", "#e65100", "#ffb74d"),
    materialPurpleLigth: createMaterialTheme("light", "#6a1b9a", "#ba68c8"),
    // color variants — Dark
    materialBlueDark:    createMaterialTheme("dark", "#1976d2", "#90caf9"),
    materialLimeDark:    createMaterialTheme("dark", "#8bc34a", "#c5e1a5"),
    materialOrangeDark:  createMaterialTheme("dark", "#ff9800", "#ffcc80"),
    materialPurpleDark:  createMaterialTheme("dark", "#9c27b0", "#ce93d8"),

    // ── Metallic (premium UI) ──────────────────────────────────────────
    metallicSilver: createMaterialTheme("light", "#607D8B", "#90A4AE", {
        background: { paper: "#ECEFF1", default: "#F7F9FA" },
        text: { primary: "#2B333A", secondary: "#5C6A73" },
    }),
    metallicGold: createMaterialTheme("light", "#B8860B", "#D4AF37", {
        background: { paper: "#FBF7EC", default: "#FDFBF4" },
        text: { primary: "#3D3418", secondary: "#7A6A38" },
    }),
    metallicRoseGold: createMaterialTheme("light", "#B76E79", "#D99AA3", {
        background: { paper: "#FBF0F1", default: "#FDF6F7" },
        text: { primary: "#4A2F33", secondary: "#8A626A" },
    }),
    metallicGunmetalDark: createMaterialTheme("dark", "#8DA1AE", "#B4C2CC", {
        background: { paper: "#2A343B", default: "#1F272C" },
        text: { primary: "#ECEFF1", secondary: "#B0BEC5" },
    }),

    // ── Advanced neutrals ──────────────────────────────────────────────
    neutralGraphiteDark: createMaterialTheme("dark", "#9AA5B1", "#C3CBD3", {
        background: { paper: "#26272B", default: "#1C1D20" },
        text: { primary: "#E8EAED", secondary: "#A8ADB4" },
    }),
    neutralSand: createMaterialTheme("light", "#A1887F", "#C7B39F", {
        background: { paper: "#F3ECE1", default: "#FAF5EC" },
        text: { primary: "#4E443C", secondary: "#7A6E63" },
    }),
    neutralCharcoalDark: createMaterialTheme("dark", "#B0BEC5", "#CFD8DC", {
        background: { paper: "#242424", default: "#171717" },
        text: { primary: "#ECECEC", secondary: "#9E9E9E" },
    }),

    // ── Pastel (soft SaaS) ─────────────────────────────────────────────
    pastelBlue: createMaterialTheme("light", "#5B8DBE", "#A7C7E7", {
        background: { paper: "#EEF4FB", default: "#F7FAFD" },
        text: { primary: "#2C3E50", secondary: "#5B6B7B" },
    }),
    pastelMint: createMaterialTheme("light", "#4FA98C", "#98D8C8", {
        background: { paper: "#EBF7F2", default: "#F5FBF8" },
        text: { primary: "#284339", secondary: "#5A756B" },
    }),
    pastelPeach: createMaterialTheme("light", "#D9825B", "#F4B58E", {
        background: { paper: "#FBF0E9", default: "#FDF7F2" },
        text: { primary: "#4E362A", secondary: "#836253" },
    }),
    pastelLavender: createMaterialTheme("light", "#8672B8", "#C3B1E1", {
        background: { paper: "#F2EEFB", default: "#F8F5FD" },
        text: { primary: "#38304A", secondary: "#6B6182" },
    }),

    // ── Vibrant (strong colour on dark) ────────────────────────────────
    vibrantCrimsonDark: createMaterialTheme("dark", "#F0475F", "#FF8A9B", {
        background: { paper: "#221317", default: "#180D10" },
        text: { primary: "#FCE9EC", secondary: "#D8A2AB" },
    }),
    vibrantEmeraldDark: createMaterialTheme("dark", "#2ECC71", "#7BE3A8", {
        background: { paper: "#12241A", default: "#0C1912" },
        text: { primary: "#E6F7EC", secondary: "#9CC7AC" },
    }),
    vibrantCyanDark: createMaterialTheme("dark", "#00D6EF", "#7FEBF7", {
        background: { paper: "#10242A", default: "#0A181C" },
        text: { primary: "#E3FAFE", secondary: "#93C4CC" },
    }),
    vibrantMagentaDark: createMaterialTheme("dark", "#FF4FB2", "#FF9AD3", {
        background: { paper: "#231323", default: "#180D18" },
        text: { primary: "#FCE7F5", secondary: "#CDA0BE" },
    }),

    // ── Futuristic (glass, neon grid, line icons) — see futuristic.ts ──
    ...futuristicThemes,
};

/**
 * Popup look shared by every theme — the standard MuiStandardDialog and any
 * raw MUI <Dialog>: the page behind is veiled in the theme's own background
 * colour and blurred, and the popup itself has no background colour, no
 * outline and no shadow — header, content and footer carry no colour of their
 * own. The veil follows the theme (light page → light veil) so the theme's
 * text colours stay readable on it; a fixed dark veil hid dark text in light
 * themes. Merged in here, once, so the many theme
 * definitions above don't each have to repeat it.
 */
function withPopupStyle(base: Theme): Theme {
    return createTheme(base, {
        components: {
            // Scrollbars stay invisible, like the sidebar menu's: thin and
            // transparent, with the thumb shown only while the pointer is over
            // that scroll area. Applied app-wide so no screen needs its own
            // scrollbar styling.
            MuiCssBaseline: {
                styleOverrides: {
                    '*': {
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'transparent transparent',
                    },
                    '*:hover': {
                        scrollbarColor: `${alpha(base.palette.text.primary, 0.28)} transparent`,
                    },
                    // Browsers without scrollbar-color (older Safari).
                    '*::-webkit-scrollbar': { width: 8, height: 8, background: 'transparent' },
                    '*::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 8 },
                    '*:hover::-webkit-scrollbar-thumb': { background: alpha(base.palette.text.primary, 0.28) },
                    '*::-webkit-scrollbar-track, *::-webkit-scrollbar-corner': { background: 'transparent' },
                    // Hub-card icon wrapper: no box of its own here; the
                    // futuristic themes turn it into a glowing tile.
                    '.fidava-hub-icon': { display: 'contents' },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        "& .MuiBackdrop-root:not(.MuiBackdrop-invisible)": {
                            backgroundColor: alpha(base.palette.background.default, 0.82),
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                        },
                    },
                    paper: {
                        // No fill and no outline: the popup sits straight on
                        // the dimmed, blurred page.
                        backgroundColor: "transparent",
                        // Dark-mode MUI paper adds an elevation gradient.
                        backgroundImage: "none",
                        border: "none",
                        boxShadow: "none",
                    },
                },
            },
            MuiDialogTitle: { styleOverrides: { root: { backgroundColor: "transparent" } } },
            MuiDialogContent: { styleOverrides: { root: { backgroundColor: "transparent" } } },
            MuiDialogActions: { styleOverrides: { root: { backgroundColor: "transparent" } } },
            // Progress bars: a neutral track. MUI tints the track with the bar's
            // own colour, so a 0% bar (the fill is correctly empty) still read as
            // a full coloured bar on every summary/dashboard (Vendas pipeline,
            // Dados and RH resumos).
            MuiLinearProgress: {
                styleOverrides: {
                    root: { backgroundColor: alpha(base.palette.text.primary, 0.08) },
                },
            },
        },
    });
}

const styled: Record<string, Theme> = {};

export default function getTheme(theme: string) {
    const base = themes[theme];
    if (!base) return base;
    if (!styled[theme]) styled[theme] = withPopupStyle(base);
    return styled[theme];
}